// src/App.jsx (Create React App)

import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuthStore, useThemeStore, useLanguageStore } from "./store";
import * as Sentry from "@sentry/react"
import Header from "./components/Header";
import AmbientePanel from "./components/ambientePanel";

// ═══════════════════════════════════════════════════════════════
// PAGES (Lazy loaded for code splitting)
// ═══════════════════════════════════════════════════════════════

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Interviews = lazy(() => import("./pages/Interviews"));
const CreateInterview = lazy(() => import("./pages/CreateInterview"));
const InterviewSession = lazy(() => import("./pages/InterviewSession"));
const InterviewResults = lazy(() => import("./pages/InterviewResults"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Settings = lazy(() => import("./pages/Settings"));

// ═══════════════════════════════════════════════════════════════
// SENTRY CONFIGURATION (without Vite)
// ═══════════════════════════════════════════════════════════════

// In CRA, environment variables are accessed like this:
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENV = process.env.REACT_APP_SENTRY_ENV;
const SENTRY_ENABLED = process.env.REACT_APP_SENTRY_ENABLED === "true";

// Initialize Sentry
if (SENTRY_ENABLED && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV || process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false,
      }),
    ],
    // In CRA, we use process.env.NODE_ENV to determine dev or prod
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

// ═══════════════════════════════════════════════════════════════
// LOADING SPINNER (for lazy loaded components)
// ═══════════════════════════════════════════════════════════════

const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "var(--bg-primary, #f5f5f5)",
    }}
  >
    <div
      style={{
        width: "40px",
        height: "40px",
        border: "4px solid #e0e0e0",
        borderTop: "4px solid #667eea",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
    <style>
      {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
    </style>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// PROTECTED ROUTE
// ═══════════════════════════════════════════════════════════════

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ═══════════════════════════════════════════════════════════════
// ERROR FALLBACK
// ═══════════════════════════════════════════════════════════════

function ErrorFallback({ error, resetError }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: "20px",
        color: "white",
      }}
    >
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          padding: "40px",
          borderRadius: "12px",
          backdropFilter: "blur(10px)",
          textAlign: "center",
          maxWidth: "500px",
        }}
      >
        <div style={{ fontSize: "64px", marginBottom: "20px" }}>
          ⚠️
        </div>

        <h1 style={{
          margin: "0 0 15px 0",
          fontSize: "28px",
          fontWeight: "600"
        }}>
          Algo salió mal
        </h1>

        <p style={{
          margin: "0 0 20px 0",
          fontSize: "16px",
          opacity: "0.9",
        }}>
          Hemos registrado este error. Por favor, recarga la página.
        </p>

        {/* Show details ONLY in development */}
        {process.env.NODE_ENV === "development" && error && (
          <details
            style={{
              margin: "20px 0",
              padding: "10px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              borderRadius: "8px",
              textAlign: "left",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: "600" }}>
              Detalles del error
            </summary>
            <pre style={{
              margin: "10px 0 0 0",
              fontSize: "12px",
              overflow: "auto",
            }}>
              {error.message}
            </pre>
          </details>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              flex: 1,
              padding: "12px 24px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Recargar página
          </button>

          <button
            onClick={() => window.location.href = "/"}
            style={{
              flex: 1,
              padding: "12px 24px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Ir a inicio
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

function App() {
  const { initializeAuth } = useAuthStore();
  const { initializeTheme } = useThemeStore();
  const { initializeLanguage } = useLanguageStore();

  useEffect(() => {
    initializeAuth();
    initializeTheme();
    initializeLanguage();

    // Configure user in Sentry
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      try {
        const userData = JSON.parse(user);
        Sentry.setUser({
          id: userData.id,
          email: userData.email,
          username: userData.username,
        });
      } catch (error) {
        // Sentry user config failed silently
      }
    }
  }, [initializeAuth, initializeTheme, initializeLanguage]);

  const token = localStorage.getItem("token");

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
      showDialog
    >
      <Router>
        <div className="app-container">
          <AmbientePanel />
          <Header />

          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interviews"
                element={
                  <ProtectedRoute>
                    <Interviews />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interviews/create"
                element={
                  <ProtectedRoute>
                    <CreateInterview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interview/:interviewId"
                element={
                  <ProtectedRoute>
                    <InterviewSession />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interviews/:interviewId/results"
                element={
                  <ProtectedRoute>
                    <InterviewResults />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* Alias en español hacia las rutas reales */}
              <Route path="/entrar" element={<Navigate to="/login" replace />} />
              <Route path="/registro" element={<Navigate to="/register" replace />} />
              <Route path="/precios" element={<Navigate to="/subscription" replace />} />
              <Route path="/entrevistas" element={<Navigate to="/interviews" replace />} />

              {/* Default Route */}
              <Route
                path="*"
                element={<Navigate to={token ? "/dashboard" : "/"} replace />}
              />
            </Routes>
          </Suspense>

          {/* Toast Notifications */}
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
        </div>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(App);
